/**
 * Композабл для управления медиа (видео/изображения) продукта.
 * Single Responsibility — только логика медиа.
 */

import { ref, computed, watch, nextTick, type Ref } from 'vue';
import type { ProductVariant } from './types/product.types';
import { isVideoFile } from '@shared/lib';

export interface UseProductMediaOptions {
  currentVariant: Ref<ProductVariant>;
}

export function useProductMedia(options: UseProductMediaOptions) {
  const { currentVariant } = options;

  const videoRefs = ref<Record<number, HTMLVideoElement | null>>({});
  const currentSlideIndex = ref(0);

  const isVideo = (url: string): boolean => isVideoFile(url);

  const mediaItems = computed(() => currentVariant.value?.img || []);
  const hasMedia = computed(() => mediaItems.value.length > 0);
  const hasMultipleMedia = computed(() => mediaItems.value.length > 1);

  const setVideoRef = (el: any, index: number) => {
    if (el) {
      videoRefs.value[index] = el;
      if (index === 0 && isVideo(mediaItems.value[0]?.url)) {
        nextTick(() => { playFirstVideo(); });
      }
    }
  };

  const playFirstVideo = async () => {
    await nextTick();
    const firstVideo = videoRefs.value[0];
    const firstMediaItem = mediaItems.value[0];
    if (firstVideo && firstMediaItem && isVideo(firstMediaItem.url)) {
      try {
        const p = firstVideo.play();
        if (p !== undefined) await p;
      } catch { /* autoplay policy */ }
    }
  };

  const handleSlideChange = async (event: any) => {
    const swiper = event?.detail?.[0];
    if (!swiper) return;

    const newIndex = swiper.activeIndex;
    const oldIndex = currentSlideIndex.value;
    currentSlideIndex.value = newIndex;

    const oldVideo = videoRefs.value[oldIndex];
    if (oldVideo) { oldVideo.pause(); oldVideo.currentTime = 0; }

    await nextTick();
    const newVideo = videoRefs.value[newIndex];
    const newMediaItem = mediaItems.value[newIndex];

    if (newVideo && newMediaItem && isVideo(newMediaItem.url)) {
      const tryPlay = async (attempts = 3) => {
        for (let i = 0; i < attempts; i++) {
          try {
            newVideo.currentTime = 0;
            const p = newVideo.play();
            if (p !== undefined) { await p; return true; }
          } catch {
            if (i < attempts - 1) await new Promise(r => setTimeout(r, 100));
          }
        }
        return false;
      };
      await tryPlay();
    }
  };

  const handleVideoLoadedMetadata = async (event: Event, index: number) => {
    const video = event.target as HTMLVideoElement;
    if (index === currentSlideIndex.value) {
      try { video.currentTime = 0; await video.play(); } catch { /* autoplay policy */ }
    }
  };

  const stopAllVideos = () => {
    for (const video of Object.values(videoRefs.value)) {
      if (video) { video.pause(); video.currentTime = 0; }
    }
  };

  const resetMedia = () => {
    stopAllVideos();
    videoRefs.value = {};
    currentSlideIndex.value = 0;
  };

  watch(currentVariant, () => {
    resetMedia();
    nextTick(() => { playFirstVideo(); });
  });

  return {
    videoRefs,
    currentSlideIndex,
    mediaItems,
    hasMedia,
    hasMultipleMedia,
    isVideo,
    setVideoRef,
    playFirstVideo,
    handleSlideChange,
    handleVideoLoadedMetadata,
    stopAllVideos,
    resetMedia,
  };
}
